// The Kierki table (.ai/kierki.md): the game of hearts/rules.ts, the 3 computer players' timing, the player's chosen
// cards to pass, the clock, and the end-of-deal window. The computer waits while the Pause window is open or the tab
// is hidden. No Undo (owner 2026-10-26): a played card stays played.
import { useSyncExternalStore } from 'react';
import {
  botPass, botPlay, cardKey, collectTrick, leaders, playCard, sameCard, startGame, startHand, submitPass, takenPoints,
  type HCard, type HeartsState,
} from './rules';
import { t } from '../i18n';
import { drawNames, fixedNames, nameList, type SeatNames } from './names';

/** Slow motion to look at the animations (owner 2026-10-26): `?slow=100` plays every Kierki animation and the
 *  computer players' timing that many times slower (up to 200); without it, 1. */
export const SLOW = (() => {
  try {
    const n = Number(new URLSearchParams(location.search).get('slow'));
    return n > 1 ? Math.min(n, 200) : 1;
  } catch {
    return 1;
  }
})();
if (SLOW > 1 && typeof Element !== 'undefined' && typeof Element.prototype.animate === 'function') {
  const animate = Element.prototype.animate;
  Element.prototype.animate = function (this: Element, ...args: Parameters<Element['animate']>) {
    const a = animate.apply(this, args);
    a.playbackRate = 1 / SLOW;
    return a;
  };
}

/** A computer player's card, and the full trick before it is taken. */
export const BOT_MS = 700;
export const TRICK_MS = 1200;
/** The last part of TRICK_MS: the full trick slides to its taker (the view's animation starts on `collecting`). */
export const COLLECT_MS = 420;
/** The deal (HeartsTable.tsx useDealAnimation): 52 cards thrown from the table's middle to the players, one after the
 *  other; the computer players wait for it. */
// as a dealer deals, one card to each player round the table, about one in the air for each player (owner 2026-10-26:
// at 300 ms, fast off the deck, the cards seemed not to come from it; with 16 cards in the air at once all four got
// theirs in bursts)
export const DEAL_STEP_MS = 70;
export const DEAL_FLIGHT_MS = 360;
export const DEAL_MS = 51 * DEAL_STEP_MS + DEAL_FLIGHT_MS + 100;
/** The exchange after the passing (HeartsTable.tsx useExchangeAnimation, owner 2026-10-26: no frame on the received
 *  cards, show them come): the player's 3 fly to their taker, the giver's 3 fly in, wait above their places, and slide
 *  down into the hand while the other cards make room; the computer players wait for it. */
export const SWAP_OUT_MS = 420;
export const SWAP_IN_AT = 300;
export const SWAP_IN_MS = 600;
export const SWAP_HOLD_MS = 600;
export const SWAP_SLIDE_MS = 300;
export const SWAP_MS = SWAP_IN_AT + SWAP_IN_MS + SWAP_HOLD_MS + SWAP_SLIDE_MS + 100;

export interface TableState {
  readonly game: HeartsState | null;
  /** The player's cards chosen to pass (up to 3). */
  readonly selected: readonly HCard[];
  /** Seconds of play in this game (the status bar's clock). */
  readonly seconds: number;
  /** Counts the deals shown (the view's deal animation starts on a change). */
  readonly deal: number;
  /** The full trick slides to its taker now (set by the store, so a paused game or a hidden tab holds it too). */
  readonly collecting: boolean;
  /** Counts the exchanges shown (the view's exchange animation starts on a change). */
  readonly swap: number;
  /** The exchange is on screen now (the hint names the giver). */
  readonly swapping: boolean;
  /** A position put in by the e2e hook (load): its end is never saved as a win. */
  readonly demo: boolean;
  /** The computer players' names for this game (names.ts; drawn anew for every game). */
  readonly names: SeatNames | null;
  /** When the game began and ended (Date.now(); the score window's New game: nextGame.ts). */
  readonly startedAt: number;
  readonly endedAt: number | null;
  /** Online (online/): when my move runs out (Date.now() ms), then the server's bot plays it; unset offline. */
  readonly deadlineAt?: number | null;
}

export interface HeartsStore {
  getState(): TableState;
  subscribe(fn: () => void): () => void;
  /** A new game: `pattern` (52 numbers, engine/shuffle.ts) gives its first deal (Retry, a report). */
  newGame(pattern: readonly number[] | null): void;
  nextHand(): void;
  toggleSelect(card: HCard): void;
  pass(): void;
  play(card: HCard): void;
  /** Stops the timers (another game was chosen). */
  stop(): void;
  /** e2e only (window.__heartsTest): show a position as if the game had reached it (the score window's tests). */
  load(g: HeartsState): void;
  /** e2e only: the game began `ms` earlier (the New game after a long game reloads, nextGame.ts). */
  backdate(ms: number): void;
}

export interface HeartsHooks {
  rng: () => number;
  /** True while the computer must wait (Pause window, hidden tab). */
  paused: () => boolean;
  /** A step for the player's report (app-entry.tsx). */
  onStep?: (what: string) => void;
  /** A deal or the game ended (the score window opens). */
  onHandEnd?: (s: HeartsState) => void;
}

export function createHeartsStore(hooks: HeartsHooks): HeartsStore {
  let state: TableState = { game: null, selected: [], seconds: 0, deal: 0, collecting: false, swap: 0, swapping: false, demo: false, names: null, startedAt: 0, endedAt: null };
  const listeners = new Set<() => void>();
  let timer: ReturnType<typeof setTimeout> | null = null;
  let clock: ReturnType<typeof setInterval> | null = null;
  const set = (patch: Partial<TableState>) => {
    state = { ...state, ...patch };
    for (const fn of listeners) fn();
  };
  const step = (what: string) => hooks.onStep?.(what);
  const clearTimer = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };
  /** Runs `fn` after `ms`, later again while the game is paused. */
  const after = (ms: number, fn: () => void) => {
    clearTimer();
    timer = setTimeout(function run() {
      if (hooks.paused()) {
        timer = setTimeout(run, 300);
        return;
      }
      timer = null;
      fn();
    }, ms * SLOW);
  };
  const startClock = () => {
    if (clock !== null) return;
    clock = setInterval(() => {
      const g = state.game;
      if (!g || g.phase === 'over' || g.phase === 'hand-end' || hooks.paused()) return;
      set({ seconds: state.seconds + 1 });
    }, 1000);
  };
  const stopClock = () => {
    if (clock !== null) clearInterval(clock);
    clock = null;
  };

  /** After every change: the computer's next card, the full trick taken, or the deal's end. */
  const advance = (g: HeartsState) => {
    set({ game: g });
    if (g.phase === 'pass') {
      // the computer players choose at once; the exchange waits for the player
      let s = g;
      for (let p = 1; p < 4; p++) if (s.passes[p] === null) s = submitPass(s, p, botPass(s.hands[p]!)) ?? s;
      if (s !== g) set({ game: s });
      return;
    }
    if (g.phase === 'play' && g.turn !== 0) {
      after(BOT_MS, () => {
        const cur = state.game!;
        if (cur.phase !== 'play' || cur.turn === 0) return;
        advance(playCard(cur, cur.turn, botPlay(cur, cur.turn))!);
      });
      return;
    }
    if (g.phase === 'trick-done') {
      after(TRICK_MS - COLLECT_MS, () => {
        set({ collecting: true });
        after(COLLECT_MS, () => {
          const cur = state.game!;
          step(`trick ${13 - cur.hands[0]!.length} to seat ${cur.winner} (${cur.trick.map((p) => cardKey(p.card)).join(' ')})`);
          set({ collecting: false });
          advance(collectTrick(cur));
        });
      });
      return;
    }
    if (g.phase === 'hand-end' || g.phase === 'over') {
      if (g.phase === 'over') set({ endedAt: Date.now() });
      step(`deal ${g.handNo + 1} ended: ${g.history[g.history.length - 1]!.join(' ')} (totals ${g.scores.join(' ')})${g.phase === 'over' ? `, game over, lowest seat ${leaders(g.scores).join(',')}` : ''}`);
      hooks.onHandEnd?.(g);
    }
  };

  /** A new deal: shown at once (the view throws the cards out), played on once the deal is over. */
  const dealt = (g: HeartsState) => {
    set({ game: g, deal: state.deal + 1 });
    after(DEAL_MS, () => advance(state.game ?? g));
  };

  return {
    getState: () => state,
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    newGame(pattern) {
      clearTimer();
      set({ selected: [], seconds: 0, collecting: false, swapping: false, demo: false, startedAt: Date.now(), endedAt: null, names: drawNames(hooks.rng, nameList(t('hearts.names')), t('hearts.you')) ?? fixedNames(t) });
      const g = startGame(pattern, hooks.rng);
      step(`kierki game, deal 1, pass ${g.passOffset}`);
      startClock();
      dealt(g);
    },
    nextHand() {
      const g = state.game;
      if (!g || g.phase !== 'hand-end') return;
      clearTimer();
      set({ selected: [], swapping: false });
      const n = startHand(g, null, hooks.rng);
      step(`deal ${n.handNo + 1}, pass ${n.passOffset}`);
      dealt(n);
    },
    toggleSelect(card) {
      const g = state.game;
      if (!g || g.phase !== 'pass' || g.passes[0] !== null) return;
      const has = state.selected.some((c) => sameCard(c, card));
      if (has) set({ selected: state.selected.filter((c) => !sameCard(c, card)) });
      else if (state.selected.length < 3) set({ selected: [...state.selected, card] });
    },
    pass() {
      const g = state.game;
      if (!g || state.selected.length !== 3) return;
      const next = submitPass(g, 0, state.selected);
      if (!next) return;
      step(`passed ${state.selected.map(cardKey).join(' ')}`);
      set({ game: next, selected: [], swap: state.swap + 1, swapping: true });
      after(SWAP_MS, () => {
        set({ swapping: false });
        advance(state.game ?? next);
      });
    },
    play(card) {
      const g = state.game;
      if (!g) return;
      const next = playCard(g, 0, card);
      if (!next) {
        step(`card refused ${cardKey(card)}`);
        return;
      }
      advance(next);
    },
    stop() {
      clearTimer();
      stopClock();
    },
    load(g) {
      clearTimer();
      set({ selected: [], collecting: false, swapping: false, demo: true });
      advance(g);
    },
    backdate(ms) {
      set({ startedAt: state.startedAt - ms });
    },
  };
}

let current: HeartsStore | null = null;
export const setHeartsStore = (s: HeartsStore) => {
  current = s;
};
export const heartsStore = (): HeartsStore => {
  if (!current) throw new Error('heartsStore: not created');
  return current;
};
export function useHearts(): TableState {
  const s = heartsStore();
  return useSyncExternalStore(s.subscribe, s.getState, s.getState);
}

/** The table as text for a report: hands (the computer's too: a report is for us), the trick, taken points, totals. */
export function heartsBoardText(st: TableState): Record<string, unknown> | null {
  const g = st.game;
  if (!g) return null;
  const k = (cards: readonly HCard[]) => cards.map(cardKey).join(' ');
  return {
    deal: g.handNo + 1, phase: g.phase, passOffset: g.passOffset, turn: g.turn, heartsBroken: g.heartsBroken,
    names: (st.names ?? fixedNames(t)).name.slice(1).join(', '), hands: g.hands.map(k), trick: g.trick.map((p) => `${p.seat}:${cardKey(p.card)}`).join(' '),
    takenPoints: takenPoints(g.taken), totals: g.scores, history: g.history, selected: k(st.selected), pattern: g.pattern.join(','),
  };
}
